import {
  Alert,
  AlertDescription,
  AlertTitle,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { PageHeader, ParticipantAvatar, StatusChip, type ChipTone } from '@/design';
import { errorMessage } from '@/domains/api';
import { idTypeLabel } from '@/domains/auth';
import { surveyApi } from '../api';
import type { IMyIdentifier, IMyParticipant, ParticipantKind } from '../api/contracts';
import { IdentifierConfirmations } from './identifier-confirmation-card';

/**
 * Profile & numbers — who this account speaks for, and every number Veritant holds about them, with
 * what the owner said about each (the two-portals design, §3.1).
 *
 * Two reads the catalogue already makes, `GET /my/participants` and `GET /my/identifiers`, drawn
 * as they came. **The owner's answer is the only state shown.** `verifiedAt` and `verifiedVia`
 * travel on the wire and are rendered nowhere, for the identifier card's reason: they say whether
 * a registry or a member of staff corroborated the number, and beside the owner's answer they
 * would read as that answer's effect (survey-api's R1 — an owner's word is never a corroboration).
 * So the words here are the owner's — "you said this is yours", "you said it is not" — and a
 * number nobody has asked about yet is said so, with the same question the catalogue asks.
 *
 * Nothing about a participant is editable here: survey-api has no profile write, and a name comes
 * from the questionnaires that ask it.
 */
export function ProfilePage() {
  const [participants, setParticipants] = React.useState<Nullable<IMyParticipant[]>>(null);
  const [identifiers, setIdentifiers] = React.useState<Nullable<IMyIdentifier[]>>(null);
  const [error, setError] = React.useState<Nullable<string>>(null);

  React.useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      surveyApi.myParticipants(controller.signal),
      surveyApi.myIdentifiers(controller.signal)
    ])
      .then(([people, numbers]) => {
        setParticipants(people);
        setIdentifiers(numbers);
      })
      .catch((loadError: unknown) => {
        if (controller.signal.aborted) return;
        setError(errorMessage(loadError, 'Your profile could not be read'));
      });
    return () => controller.abort();
  }, []);

  const answered = (row: IMyIdentifier) =>
    setIdentifiers(
      (current) =>
        current?.map((held) => (held.identifierId === row.identifierId ? row : held)) ?? current
    );

  return (
    <>
      <PageHeader
        title="Profile & numbers"
        lede="Who this account speaks for, and the numbers Veritant holds about each of them."
      />

      <div className="mx-auto grid w-full max-w-[1120px] gap-6 px-6 pt-6 pb-16 lg:px-10">
        {error ? (
          <Alert variant="destructive">
            <AlertTitle>Something went wrong</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : participants === null || identifiers === null ? (
          <Skeleton className="h-48 w-full" />
        ) : (
          <>
            <IdentifierConfirmations
              identifiers={identifiers}
              participants={participants}
              onAnswered={answered}
            />

            {participants.length === 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle>This account speaks for nobody yet</CardTitle>
                  <CardDescription>
                    A person, a team or a firm is linked to an account when it registers, or
                    when Veritant’s research team links it.
                  </CardDescription>
                </CardHeader>
              </Card>
            ) : (
              participants.map((participant) => (
                <ParticipantCard
                  key={participant.participantId}
                  participant={participant}
                  numbers={identifiers.filter(
                    (row) => row.participantId === participant.participantId
                  )}
                />
              ))
            )}
          </>
        )}
      </div>
    </>
  );
}

const KIND: Record<ParticipantKind, string> = {
  person: 'A person',
  team: 'A team',
  org: 'A firm'
};

function ParticipantCard({
  participant,
  numbers
}: {
  participant: IMyParticipant;
  numbers: IMyIdentifier[];
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-4">
          <ParticipantAvatar
            photoUri={participant.photoUri}
            displayName={participant.displayName}
            size="lg"
            decorative
          />
          <div className="grid min-w-0 gap-1.5">
            <CardDescription>{KIND[participant.kind] ?? participant.kind}</CardDescription>
            <CardTitle className="text-xl font-normal text-[var(--color-royal-blue)]">
              {participant.displayName}
            </CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {numbers.length === 0 ? (
          <p className="text-[0.9375rem] text-muted-foreground">No number is held about them.</p>
        ) : (
          <table className="w-full text-left text-[0.9375rem]">
            <caption className="sr-only">Numbers held about {participant.displayName}</caption>
            <thead>
              <tr className="border-b border-border text-sm text-muted-foreground">
                <th scope="col" className="py-2 pr-4 font-normal">
                  Number
                </th>
                <th scope="col" className="py-2 pr-4 font-normal">
                  Value
                </th>
                <th scope="col" className="py-2 font-normal">
                  What you told us
                </th>
              </tr>
            </thead>
            <tbody>
              {numbers.map((row) => {
                const said = saidOf(row);
                return (
                  <tr key={row.identifierId} className="border-b border-border last:border-0">
                    <td className="py-3 pr-4">{idTypeLabel(row.idType)}</td>
                    <td className="py-3 pr-4 tabular-nums">{row.value}</td>
                    <td className="py-3">
                      <StatusChip tone={said.tone} dot>
                        {said.label}
                      </StatusChip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

function saidOf(row: IMyIdentifier): { label: string; tone: ChipTone } {
  switch (row.selfConfirmation) {
    case 'confirmed':
      return { label: 'You said this is yours', tone: 'success' };
    case 'denied':
      return { label: 'You said this is not yours', tone: 'attention' };
    default:
      return { label: 'Not asked yet', tone: 'neutral' };
  }
}
